import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { GridRowModes } from "@mui/x-data-grid";
import { Backdrop } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";

import { RolePermissions, PermissionContext } from "appContext";
import { endpoint } from "views/pages/manage/common/constant";
import { getAPI, putpost, masterServiceBaseUrl } from "services/http";
import {
  failureToast,
  successToast,
} from "../../../common/utils/methods/toasterFunctions/function";
import { fetchAllPromisedData } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { setMasterDropdowns } from "redux/globalData/globalDataAction";
import ALoader from "views/pages/manage/common/a-loader";

import { pages } from "views/pages/manage/common/constant";
import { isObjEmpty } from "../../pincode/constants";
import CustomButton from "components/CustomButton";
import TableContent from "./TableContent";

const InvoiceEntry = () => {
  const { id } = useParams();

  const dispatch = useDispatch();
  const masterDropdowns = useSelector(
    (state) => state.globalData.masterDropdowns,
  );

  const permissions = useContext(PermissionContext);
  const userPermissions = RolePermissions(
    permissions,
    pages["itemTypeSetup"]["id"],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [headerData, setHeaderData] = useState({});
  const [accountOptions, setAccountOptions] = useState([]);
  const [positiveRows, setPositiveRows] = useState([]);
  const [negativeRows, setNegativeRows] = useState([]);
  const [rowForm, setRowForm] = useState({});
  const [positiveRowModesModel, setPositiveRowModesModel] = useState({});
  const [negativeRowModesModel, setNegativeRowModesModel] = useState({});

  useEffect(() => {
    if (masterDropdowns?.elementType?.length) {
      const currItem = masterDropdowns.elementType.find((obj) => obj.id == id);

      setHeaderData({
        itemType: currItem?.itemTypeId,
        itemTypeElementDesc: currItem?.description,
      });
    }

    if (masterDropdowns?.allActiveBankAccounts?.length) {
      setAccountOptions(masterDropdowns?.allActiveBankAccounts);
    } else fetchBankAccounts();
  }, [masterDropdowns?.allActiveBankAccounts]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchBankAccounts = async () => {
    const response = await fetchAllPromisedData(
      `${endpoint.allActiveBankAccounts}`,
    );

    const options = response?.paginatedData?.map((obj) => ({
      ...obj,
      label: obj.accountNumber,
      value: obj.accountNumber,
    }));

    dispatch(
      setMasterDropdowns({
        allActiveBankAccounts: options,
      }),
    );
  };

  // Fetch API
  const fetchData = () => {
    getAPI(
      `${masterServiceBaseUrl}/itemTypeInvoiceEntries/${id}`,
      (res) => {
        const data = res?.data || {};

        setPositiveRows(data?.positiveEntries || []);
        setNegativeRows(data?.negativeEntries || []);
      },
      (err) => failureToast(err.message),
    );
  };

  // Edit Controls
  const startEditing = (rowId, row, type) => {
    if (type === "POSITIVE") {
      setPositiveRowModesModel((prev) => ({
        ...prev,
        [rowId]: { mode: GridRowModes.Edit },
      }));
    } else {
      setNegativeRowModesModel((prev) => ({
        ...prev,
        [rowId]: { mode: GridRowModes.Edit },
      }));
    }

    setRowForm(row);
  };

  const stopEditing = (rowId, type) => {
    const currForm = { ...rowForm };

    if (type === "POSITIVE") {
      setPositiveRowModesModel((prev) => ({
        [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
    } else {
      setNegativeRowModesModel((prev) => ({
        [currForm?.id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
    }

    setRowForm({});
  };

  // Duplicate Validation
  const isDuplicate = (row, list, type) => {
    return list.some(
      (r) =>
        r.id !== row.id &&
        r.sfglAccountType === row.sfglAccountType &&
        moment(r.effectiveDate).format("DD/MM/YYYY") ===
          moment(row.effectiveDate).format("DD/MM/YYYY") &&
        r.status === row.status &&
        (type === "POSITIVE"
          ? r.paymentStatus === row.paymentStatus
          : r.creditNoteType === row.creditNoteType),
    );
  };

  // Save
  const saveRow = (rowId, type) => {
    setIsLoading(true);
    const list = type === "POSITIVE" ? positiveRows : negativeRows;

    if (isDuplicate(rowForm, list, type)) {
      failureToast("Duplicate active configuration not allowed");
      setIsLoading(false);
      return;
    }

    const payload = {
      itemTypeSetupId: id,
      entryType: type,
      paymentStatus: rowForm?.paymentStatus,
      creditNoteType: rowForm?.creditNoteType,
      sfglAccountType: rowForm.sfglAccountType,
      sfglAccountKey: rowForm.sfglAccountKey,
      debitCredit: rowForm.debitCredit,
      accountNumber: rowForm.accountNumber,
      description: rowForm.description,
      effectiveDate: rowForm.effectiveDate,
      status: rowForm.status,
    };

    const url = rowForm?.isNew
      ? `${masterServiceBaseUrl}/itemTypeInvoiceEntries`
      : `${masterServiceBaseUrl}/itemTypeInvoiceEntries/${rowId}`;

    const method = rowForm?.isNew ? "post" : "put";

    putpost(
      url,
      (res) => {
        successToast(res.message);
        fetchData();
        stopEditing(rowId, type);
        setIsLoading(false);
      },
      (err) => {
        setIsLoading(false);
        return failureToast(err.message);
      },
      payload,
      method,
    );
  };

  // Add Row
  const addNewRow = (type) => {
    if (!isObjEmpty(rowForm)) {
      failureToast("Please Delete or Save current changes");
      return;
    }

    const newId = uuidv4();

    const newRow = {
      id: newId,
      entryType: type,
      paymentStatus: "",
      creditNoteType: "",
      sfglAccountType: "",
      debitCredit: "",
      accountNumber: "",
      description: "",
      effectiveDate: new Date().toISOString(),
      status: "ACTIVE",
      isNew: true,
    };

    if (type === "POSITIVE") {
      setPositiveRows((prev) => [newRow, ...prev]);
    } else {
      setNegativeRows((prev) => [newRow, ...prev]);
    }

    // setTimeout(() => {
    startEditing(newId, newRow, type);
    // }, 0);
  };

  const handleCancel = (params, type) => {
    stopEditing(params.id, type);

    if (params.row.isNew) {
      if (type === "POSITIVE") {
        const arr = positiveRows.filter((r) => r.id !== params.id);
        setPositiveRows(arr);
      } else {
        setNegativeRows((prev) => prev.filter((r) => r.id !== params.id));
      }
    }

    setRowForm({});
  };

  return (
    <div className="p-4">
      {isLoading && (
        <Backdrop
          open={isLoading}
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1300,
          })}
        >
          <ALoader />
        </Backdrop>
      )}
      {/* 🔹 HEADER */}
      <div>
        {/* <div
          style={{ cursor: "pointer", color: "#4a6cf7" }}
          onClick={() => history.goBack()}
        >
          ← Back
        </div> */}

        <h2 className="my-2 text-bold">
          {headerData?.itemType} | {headerData?.itemTypeElementDesc}
        </h2>

        <small style={{ color: "#6c757d" }}>
          Configure account entries for invoicing
        </small>

        <hr className="m-4" />
      </div>

      {/* POSITIVE SECTION */}
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h3 className="text-bold mb-1">Positive Invoice Account Entries</h3>

            <small style={{ color: "#6c757d" }}>
              Configure positive account entries for standard invoicing
            </small>
          </div>

          <CustomButton
            content="+ Add New Row"
            permissionType={"C"}
            permissionSet={userPermissions}
            onClick={() => addNewRow("POSITIVE")}
          />
        </div>

        <TableContent
          type="POSITIVE"
          rows={positiveRows}
          rowForm={rowForm}
          setRowForm={setRowForm}
          rowModesModel={positiveRowModesModel}
          startEditing={(id, row) => startEditing(id, row, "POSITIVE")}
          saveRow={saveRow}
          handleCancel={handleCancel}
          accountOptions={accountOptions}
          userPermissions={userPermissions}
          isLoading={isLoading}
        />
      </div>

      {/* NEGATIVE SECTION */}
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h4 className="text-bold mb-1">Negative Invoice Account Entries</h4>

            <small style={{ color: "#6c757d" }}>
              Configure negative account entries for refunds and adjustments
            </small>
          </div>

          <CustomButton
            content="+ Add New Row"
            permissionType={"C"}
            permissionSet={userPermissions}
            onClick={() => addNewRow("NEGATIVE")}
          />
        </div>

        <TableContent
          type="NEGATIVE"
          rows={negativeRows}
          rowForm={rowForm}
          setRowForm={setRowForm}
          rowModesModel={negativeRowModesModel}
          accountOptions={accountOptions}
          startEditing={(id, row) => startEditing(id, row, "NEGATIVE")}
          saveRow={saveRow}
          handleCancel={handleCancel}
          userPermissions={userPermissions}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default InvoiceEntry;
